import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  getExams,
  createTest,
  deleteTest,
  addTestToExam,
  uploadTestAdditionalInformation,
} from "../../services/Exams/ExamsService";
import KaTeXComponent from "../../components/KaTeXComponent/KaTeXComponent";
import HelpButton from "../../components/Help/HelpButton";
import TestsGuide from "../../components/Help/guides/TestsGuide";
import usePassagesAvailable from "../../components/Passages/usePassagesAvailable";
import i18n from "../../lang/i18n";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { isArchived } from "./ExamStatus";
import { backLinkClass, quietButtonClass } from "./TestBuilder/ui";
import TestHeadingField, { headingErrorText } from "./TestBuilder/TestHeadingField";

// Fields the create request can refuse one by one (400 {field: [messages]}): shown under them.
const FIELD_ERRORS = ['name', 'heading', 'duration_minutes'];

// Shared presentation classes for this page
const fieldClass =
  'block w-full rounded-lg px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500';
const fieldOk = 'border-slate-300 hover:border-slate-400 focus:border-blue-600 focus:ring-blue-600/15';
const fieldError = 'border-red-300 hover:border-red-400 focus:border-red-500 focus:ring-red-500/15';
const quillClass =
  'rounded-lg bg-white shadow-sm [&_.ql-container]:!border-slate-300 [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[8rem] [&_.ql-editor]:text-sm [&_.ql-toolbar]:!border-slate-300 [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:bg-slate-50';

const CreateTest = () => {
  const [t] = useTranslation();
  const { uuid: examUuid } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    heading: '',
    duration_minutes: 60,
    is_active: true,
    calculator_is_allowed: false,
    periodic_table_is_allowed: false,
    additional_information: '',
  });
  const [formErrors, setFormErrors] = useState({});
  // After a refused submit the first refused field takes the focus, once the form is enabled again (it scrolls
  // into view: the page heading can be off screen on a phone).
  const [errorFocus, setErrorFocus] = useState(0);
  const formRef = useRef(null);
  const [step, setStep] = useState(''); // For displaying current creation step
  const [aiFile, setAiFile] = useState(null);
  const aiFileInputRef = useRef(null);
  // The test created by a submit whose "add to exam" step failed: a retry only adds it instead of creating a
  // duplicate, and the form stays locked since its values would no longer reach that test.
  const [createdTestUuid, setCreatedTestUuid] = useState(null);
  // An archived exam takes no test (409 exam_archived): the form is shown, but nothing is created for it.
  const archived = isArchived(exam);
  const formLocked = creating || Boolean(createdTestUuid) || archived;
  // The test was created, then the exam turned out archived (meanwhile, e.g. in another tab): the test stays in the
  // test bank, unlinked, and the page offers to open or delete it instead of a retry that can't succeed.
  const strandedTest = archived && createdTestUuid;
  const [deletingTest, setDeletingTest] = useState(false);
  const passages = usePassagesAvailable();

  useEffect(() => {
    const fetchExam = async () => {
      try {
        setLoading(true);
        console.log('Fetching exam with UUID:', examUuid);
        
        const data = await getExams({ params: { uuid: examUuid } });
        console.log('Exam data for test creation:', data);
        
        if (data) {
          setExam(data);
        } else {
          setError(i18n.t('test_bank.exam_create.exam_not_found'));
        }
      } catch (err) {
        setError(i18n.t('test_bank.exam_create.load_error'));
        console.error('Error fetching exam:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (examUuid) {
      fetchExam();
    } else {
      setLoading(false);
      setError(i18n.t('test_bank.exam_create.uuid_not_found'));
    }
  }, [examUuid]);

  useEffect(() => {
    if (errorFocus) formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [errorFocus]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = t('test_bank.form.name_required');
    }
    
    if (formData.duration_minutes <= 0) {
      errors.duration_minutes = t('test_bank.form.duration_positive');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleBackToExam = () => {
    navigate(`/exams_list/${examUuid}`);
  };

  const handleAiFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error(t('test_bank.form.only_docx'));
      e.target.value = '';
      return;
    }
    const hasContent =
      formData.additional_information &&
      formData.additional_information.replace(/<[^>]*>/g, '').trim();
    if (hasContent) {
      const confirmed = window.confirm(
        t('test_bank.form.confirm_replace_manual'),
      );
      if (!confirmed) {
        e.target.value = '';
        return;
      }
    }
    setAiFile(file);
  };

  const handleClearAiFile = () => {
    setAiFile(null);
    if (aiFileInputRef.current) aiFileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (archived) return;
    if (!validateForm()) {
      setErrorFocus((n) => n + 1);
      return;
    }

    let testUuid = createdTestUuid;
    try {
      setCreating(true);
      setError(null);

      if (!testUuid) {
        const testData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          heading: formData.heading.trim(),
          duration_minutes: formData.duration_minutes,
          is_active: formData.is_active,
          calculator_is_allowed: formData.calculator_is_allowed,
          periodic_table_is_allowed: formData.periodic_table_is_allowed,
          // When a file will be uploaded it replaces whatever we send here
          additional_information: aiFile ? '' : formData.additional_information,
          ...(exam?.category && { category: exam.category }),
          ...(exam?.subject && { subject: exam.subject }),
          ...(exam?.topic && { topic: exam.topic }),
          ...(exam?.subtopic && { subtopic: exam.subtopic }),
        };

        setStep(t('test_bank.exam_create.step_creating'));
        const createdTest = await createTest({ data: testData });
        testUuid = createdTest.uuid;
        setCreatedTestUuid(testUuid);

        if (aiFile) {
          setStep(t('test_bank.exam_create.step_uploading'));
          try {
            await uploadTestAdditionalInformation({
              params: { uuid: testUuid },
              file: aiFile,
            });
          } catch (err) {
            console.error('Failed to upload additional information:', err);
            toast.error(t('test_bank.form.upload_failed_after_create'));
          }
        }
      }

      setStep(t('test_bank.exam_create.step_adding'));
      const nextOrder = (exam?.exam_tests?.length || 0) + 1;
      
      const examTestData = {
        exam: examUuid,
        test: testUuid,
        order: nextOrder,
        weight: 1,
        is_required: true
      };
      await addTestToExam({ params: { uuid: examUuid }, data: examTestData });

      // Straight to adding questions; Back there returns to the exam (replace: Back must not reopen this form).
      navigate(`/test/${testUuid}`, {
        replace: true,
        state: { justCreated: true, from: `/exams_list/${examUuid}` },
      });
    } catch (err) {
      console.error('Error:', err);
      const data = err?.response?.data;
      if (!testUuid && data && typeof data === 'object' && !data.code && FIELD_ERRORS.some((key) => data[key])) {
        // The test was not created: what the server refused shows under its field.
        setFormErrors(
          Object.fromEntries(
            FIELD_ERRORS.map((key) => [key, (key === 'heading' ? headingErrorText(t, data[key]) : [].concat(data[key] || []).join(' ')) || null])
          )
        );
        setError(null);
        setErrorFocus((n) => n + 1);
        return;
      }
      if (testUuid && getApiErrorCode(err) === 'exam_archived') {
        // The exam's archive banner says why; the test left over is said below it, with what to do with it.
        setExam((prev) => ({ ...prev, status: 'archived' }));
        setError(null);
        return;
      }
      // Once the test exists only "add to exam" failed, and the retry repeats just that step.
      setError(getApiErrorMessage(t, err, testUuid ? 'test_bank.exam_create.add_failed' : 'test_bank.exam_create.generic_error'));
    } finally {
      setCreating(false);
      setStep('');
    }
  };
  
  // The test left over by an exam archived meanwhile, deleted on request; the page then goes back to the exam.
  const handleDeleteStranded = async () => {
    if (!window.confirm(t('test_bank.list.confirm_delete'))) return;
    setDeletingTest(true);
    try {
      await deleteTest({ params: { uuid: createdTestUuid } });
      toast.success(t('test_bank.list.deleted'));
      navigate(`/exams_list/${examUuid}`, { replace: true });
    } catch (err) {
      console.error('Error deleting test:', err);
      setError(getApiErrorMessage(t, err, 'test_bank.list.delete_error'));
      setDeletingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="motion-safe:animate-fade-in">
        <div className="flex h-64 items-center justify-center gap-3 text-sm text-slate-500">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
          <span>{t('test_bank.exam_create.loading_exam')}</span>
        </div>
      </div>
    );
  }

  if (error && !exam) {
    return (
      <div className="motion-safe:animate-fade-in">
        <div className="mx-auto max-w-2xl">
          <button
            onClick={() => navigate('/exams_list')}
            className="mb-4 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium text-slate-500 transition duration-200 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t('test_bank.exam_create.back_to_exams')}
          </button>
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center">
              <div className="mb-1 text-base font-semibold text-red-600">{t('test_bank.common.error')}</div>
              <div className="text-sm text-slate-500">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="motion-safe:animate-fade-in">
      <div className="mx-auto max-w-2xl">
        {/* Back button: the way back to the exam (the form has no Cancel of its own: it would do the same). Not while
            the test is being created or deleted, as that Cancel did. */}
        <button
          type="button"
          onClick={handleBackToExam}
          disabled={creating || deletingTest}
          className={`${backLinkClass} -ml-1.5 mb-3 disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t('test_bank.common.back_to_exam')}
        </button>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Header, with the guide's "?" right after the title */}
          <div className="border-b border-slate-200 p-5 sm:p-6">
            <div className="flex min-w-0 items-center gap-1.5">
              <h1 className="min-w-0 text-2xl font-semibold tracking-tight text-slate-900">
                {t('test_bank.form.create_new_test')}
              </h1>
              <HelpButton id="tests" title={t('help.tests.title')} className="-my-1.5 md:-my-0.5">
                <TestsGuide passages={passages} />
              </HelpButton>
            </div>
            {exam && (
              <p className="mt-1 text-sm text-slate-500">
                {t('test_bank.exam_create.for_exam', { name: exam.name })}
              </p>
            )}
          </div>

          <div className="p-5 sm:p-6">
          {archived && (
            <div role="alert" className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-700">{t('errors.exam_archived')}</p>
              {strandedTest && (
                <>
                  <p className="mt-2 text-sm text-slate-700">{t('test_bank.exam_create.created_exam_archived')}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={`/test/${createdTestUuid}`}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                      {t('test_bank.exam_create.open_test')}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                    </Link>
                    <button
                      type="button"
                      onClick={handleDeleteStranded}
                      disabled={deletingTest}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-white px-4 text-sm font-medium text-red-700 shadow-sm ring-1 ring-inset ring-red-200 transition duration-200 enabled:hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      {t('test_bank.list.delete_test')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Error message */}
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form: Enter in a one-line field creates the test, as on /test_create */}
          <form
            ref={formRef}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-6"
          >
            {/* Test name */}
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('test_bank.form.test_name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('test_bank.form.test_name_placeholder')}
                disabled={formLocked}
                aria-invalid={formErrors.name ? true : undefined}
                aria-describedby={formErrors.name ? 'name-error' : undefined}
                className={`${fieldClass} h-10 ${
                  formErrors.name ? fieldError : fieldOk
                }`}
              />
              {formErrors.name && (
                <p id="name-error" className="mt-1.5 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('test_bank.form.description')}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('test_bank.form.description_placeholder_short')}
                rows={4}
                disabled={formLocked}
                className={`${fieldClass} ${fieldOk} py-2`}
              />
            </div>

            {/* What the exam's Word prints at the top of the test's pages */}
            <TestHeadingField
              id="heading"
              value={formData.heading}
              onChange={handleInputChange}
              error={formErrors.heading || ''}
              disabled={formLocked}
            />

            {/* Duration */}
            <div>
              <label htmlFor="duration_minutes" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('test_bank.form.duration_minutes')}
              </label>
              <input
                type="number"
                id="duration_minutes"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleInputChange}
                min="1"
                disabled={formLocked}
                aria-invalid={formErrors.duration_minutes ? true : undefined}
                aria-describedby={formErrors.duration_minutes ? 'duration_minutes-error' : undefined}
                className={`${fieldClass} h-10 tabular-nums sm:max-w-[12rem] ${
                  formErrors.duration_minutes ? fieldError : fieldOk
                }`}
              />
              {formErrors.duration_minutes && (
                <p id="duration_minutes-error" className="mt-1.5 text-sm text-red-600">{formErrors.duration_minutes}</p>
              )}
            </div>

            {/* Calculator toggle */}
            <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
              <input
                type="checkbox"
                checked={formData.calculator_is_allowed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    calculator_is_allowed: e.target.checked,
                  }))
                }
                disabled={formLocked}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-slate-700">{t('test_bank.form.calculator_allowed')}</span>
            </label>

            {/* Periodic table toggle */}
            <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
              <input
                type="checkbox"
                checked={formData.periodic_table_is_allowed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    periodic_table_is_allowed: e.target.checked,
                  }))
                }
                disabled={formLocked}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-slate-700">{t('test_bank.form.periodic_table_allowed')}</span>
            </label>

            {/* Additional information */}
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label className="block text-sm font-medium text-slate-700">
                  {t('test_bank.form.additional_information')}
                </label>
                <div className="flex min-w-0 items-center gap-2">
                  {aiFile && (
                    <span className="flex min-w-0 items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs font-medium text-slate-700">
                      <span className="max-w-[200px] truncate">{aiFile.name}</span>
                      <button
                        type="button"
                        onClick={handleClearAiFile}
                        disabled={formLocked}
                        className="flex h-5 w-5 items-center justify-center rounded-full text-sm leading-none text-slate-400 transition duration-200 hover:bg-red-100 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-600 disabled:opacity-50"
                        aria-label={t('test_bank.form.remove_file')}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {/* A secondary way to fill it in: quiet. */}
                  <button
                    type="button"
                    onClick={() => aiFileInputRef.current?.click()}
                    disabled={formLocked}
                    className={`${quietButtonClass} -mr-3`}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                    {aiFile ? t('test_bank.form.replace_file') : t('test_bank.form.upload_docx')}
                  </button>
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    accept=".docx"
                    onChange={handleAiFileChange}
                    className="hidden"
                  />
                </div>
              </div>
              <ReactQuill
                theme="snow"
                value={formData.additional_information}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, additional_information: value }))
                }
                readOnly={formLocked || Boolean(aiFile)}
                className={quillClass}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {aiFile
                  ? t('test_bank.form.file_will_replace')
                  : t('test_bank.form.formulas_hint')}
              </p>
              {!aiFile && formData.additional_information && formData.additional_information.replace(/<[^>]*>/g, '').trim() && (
                <div className="mt-3">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">{t('test_bank.form.preview')}</p>
                  <div className="prose prose-sm max-h-60 max-w-none overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <KaTeXComponent text={formData.additional_information} />
                  </div>
                </div>
              )}
            </div>

            {/* The one main button (the back link above leaves without creating); a test left over by an exam
                archived meanwhile has its own actions above instead. */}
            {!strandedTest && (
            <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
              <button
                type="submit"
                disabled={creating || archived}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 enabled:hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none sm:h-10"
              >
                {creating && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                )}
                {creating
                  ? (step || t('test_bank.form.creating'))
                  : createdTestUuid ? t('test_bank.builder.retry') : t('test_bank.single_create.submit_and_add')}
                {!creating && <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>
            )}
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTest;
